import { isMobileDevice } from '../CordovaService/NetworkConnectionService';
// import { hasRequiredSpaceInBytes } from '../CordovaService/StorageService';
import { getAllFilesRemote, getFileRemote } from '../DBService/DbService';
import { getApiBaseUrl } from '../DBService/DbService';

const umlautMap: { [key: string]: string } = {
    '\u00dc': 'UE',
    '\u00c4': 'AE',
    '\u00d6': 'OE',
    '\u00fc': 'ue',
    '\u00e4': 'ae',
    '\u00f6': 'oe',
    '\u00df': 'ss',
};

const replaceUmlauts = (str: string) => {
    return str
        .replace(/[\u00dc|\u00c4|\u00d6][a-z]/g, (a) => {
            const big = umlautMap[a.slice(0, 1)];
            return big.charAt(0) + big.charAt(1).toLowerCase() + a.slice(1);
        })
        .replace(new RegExp('[' + Object.keys(umlautMap).join('|') + ']', 'g'),
            (a) => umlautMap[a]
        );
};

const getMediaDirectory = () => {
    return (window.cordova as any).file.dataDirectory + 'Media/';
};

const makeSureMediaDirectoryExists = (): Promise<void> => {
    return new Promise((resolve) => {
        (window as any).resolveLocalFileSystemURL(
            (window.cordova as any).file.dataDirectory,
            (directoryEntry: FileSystemDirectoryEntry) => {
                directoryEntry.getDirectory(
                    'Media/', { create: true, exclusive: true },
                    (dirEntry: FileSystemDirectoryEntry) => {
                        console.log('Mediadirectory successfully created', dirEntry);
                        resolve();
                    },
                    (error: Error) => {
                        resolve();
                    }
                );
            }
        );
    });
};

const writeToLocalFile = (fileName: string, data: any): Promise<void> => {
    return new Promise((resolve, reject) => {
        data = JSON.stringify(data, null, '\t');
        (window as any).resolveLocalFileSystemURL(
            getMediaDirectory(),
            function (directoryEntry: any) {
                directoryEntry.getFile(
                    replaceUmlauts(fileName),
                    { create: true },
                    function (fileEntry: any) {
                        fileEntry.createWriter(function (fileWriter: any) {
                            fileWriter.onwriteend = function (e: any) {
                                resolve();
                            };

                            fileWriter.onerror = function (e: any) {
                                reject('Write failed: ' + e.toString());
                            };

                            var blob = new Blob([data], { type: 'text/plain' });
                            fileWriter.write(blob);
                        }, () => reject('CreateWriter: Failed to write file ' + fileName));
                    },
                    () => reject('GetFile: Failed to write file ' + fileName)
                );
            },
            () => reject('ResolveLocalFileSystemURL: Failed to write file ' + fileName)
        );
    });
};

export const readFromLocalFile = (fileName: string): Promise<any> => {
    return new Promise((resolve, reject) => {
        const pathToFile = getMediaDirectory() + replaceUmlauts(fileName);
        (window as any).resolveLocalFileSystemURL(
            pathToFile,
            function (fileEntry: any) {
                fileEntry.file(function (file: any) {
                    const reader = new FileReader();

                    reader.onloadend = function (e: any) {
                        resolve(e.target.result);
                    };

                    reader.readAsText(file);
                }, () => reject('fileEntry.File: Failed to read file: ' + fileName));
            },
            () => reject('ResolveLocalFileSystemURL: Failed to read file: ' + fileName)
        );
    });
};

const getAllFilenames = (): Promise<string[]> => {
    return new Promise((resolve, reject) => {
        (window as any).resolveLocalFileSystemURL(
            getMediaDirectory(),
            (directionEntry: any) => {
                const reader = directionEntry.createReader();
                reader.readEntries(
                    (entries: any) => {
                        resolve(entries.map((entry: FileSystemEntry) => entry.name));
                    },
                    (error: any) => reject('readEntries: Failed: ' + error)
                );
            },
            (error: any) => reject('ResolveLocalFileSystemURL: ' + error)
        );
    });
};

const removeLocalFile = (fileName: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        (window as any).resolveLocalFileSystemURL(
            getMediaDirectory(),
            function (directoryEntry: any) {
                directoryEntry.getFile(
                    replaceUmlauts(fileName),
                    { create: false },
                    function (fileEntry: any) {
                        fileEntry.remove(() => {
                            resolve();
                        }, (error: any) => reject('Failed to remove file ' + fileName + ': ' + error));
                    },
                    () => reject('GetFile: Failed to remove file ' + fileName)
                );
            },
            () => reject('ResolveLocalFileSystemURL: Failed to remove file ' + fileName)
        );
    });
};

const localFileExists = async (fileName: string): Promise<boolean> => {
    return new Promise((resolve) => {
        const pathToFile = getMediaDirectory() + replaceUmlauts(fileName);
        (window as any).resolveLocalFileSystemURL(
            pathToFile,
            () => resolve(true),
            () => resolve(false)
        );
    });
};

const loadFileFromRemoteDbToLocalStorage = async (id: string, studyId: string | undefined): Promise<void> => {
    const dbRes = await getFileRemote(id, studyId);
    if (dbRes?._attachments) {
        const revision = dbRes._rev;
        const filename = Object.keys(dbRes._attachments)[0];
        const filedata = (dbRes._attachments[filename] as any).data;
        if (filedata.length > 3000000) {
            throw new Error('File ' + id + ' is too large to store locally');
        }
        const dataUrl = `data:${dbRes._attachments[filename].content_type};base64,${filedata}`;
        await writeToLocalFile(id, { _rev: revision, dataUrl });
    } else {
        throw new Error('File ' + id + ' does not exist on remote db.');
    }
};

const removeLocalFileIfExists = async (fileName: string): Promise<void> => {
    if (await localFileExists(fileName)) {
        try {
            await removeLocalFile(fileName);
            console.log('Removed file ' + fileName);
        } catch (e) {
            console.error('Failed to remove file ' + fileName + ': ' + e);
        }
    }
};

const syncFileChanges = async (studyId: string | undefined, forceLoadAllFiles?: boolean) => {
    console.log('syncronizing media files from db, forceLoad: ' + !!forceLoadAllFiles);
    const files = await getAllFilesRemote(studyId);

    for (const file of files) {
        if (file.value.deleted) {
            removeLocalFileIfExists(file.id);
        }

        let newChange: boolean = false;
        try {
            const localFile = JSON.parse(await readFromLocalFile(file.id));
            if (localFile._rev !== file.value.rev) {
                newChange = true;
            }
        } catch (e) {
            newChange = true; // file does not exist
        }
        try {
            if (newChange || forceLoadAllFiles) {
                console.log('loading file ' + file.id + '...');
                await loadFileFromRemoteDbToLocalStorage(file.id, studyId);
            }
        } catch (e) {
            console.error('Something went wrong while loading file ' + file.id + ': ' + e.toString());
        }
    }

    const localFileNames = (await getAllFilenames()).map(filename => replaceUmlauts(filename));
    const remoteFileNames = files.map(file => replaceUmlauts(file.id));
    const deletableFilenames = localFileNames.filter(local => !remoteFileNames.includes(local));
    for (const deletable of deletableFilenames) {
        await removeLocalFileIfExists(deletable);
    }

    console.log('syncronizing of all files done.');
};

export const getDataUrl = async (id: string, studyId: string | undefined): Promise<string> => {
    if (isMobileDevice()) {
        console.log('loading file ' + id + ' from local storage');
        try {
            const localData = JSON.parse(await readFromLocalFile(id));
            const dataUrl = localData.dataUrl;
            return dataUrl;
        } catch (e) {
            console.log('File not loaded from local stroage due to: ' + e);
        }
    }
    // Fallback: use streaming media proxy (handles large files not cached locally)
    return `${getApiBaseUrl()}/media/${studyId}/${id}`;
};

export const syncMediaToFileSystem = async (studyId: string | undefined) => {
    await makeSureMediaDirectoryExists();
    await syncFileChanges(studyId);
};

export const syncMediaIfProperNetworkConnection = (studyId: string | undefined) => {
    const connectionType = (<any>navigator).connection.type;
    if (connectionType === 'wifi' || (connectionType as any) === '4g') {
        console.log('online with ' + connectionType + ', starting media sync...');
        syncMediaToFileSystem(studyId);
    }
};

// helper, just to check file sizes, be careful, has to download every file with attachment
// export const getMediaDataBySize = async () => {
//     const allFiles = (await Promise.all(await (await getAllFilesRemote()).map(async f => {
//         const file = await getFileRemote(f.id);
//         if (file?._attachments) {
//             const fileName = Object.keys(file?._attachments)[0];
//             // console.log(`File ${fileName} has a length of ${(file._attachments[fileName] as any).data.length}`);
//             // console.log((file._attachments[fileName] as any));
//             return { name: f.id, size: (file._attachments[fileName] as any).data.length };
//         } else {
//             // console.log('Failed to get Filesizes');
//             return { name: f.id, size: -1 };
//         }
//     }))).sort((a, b) => a.size - b.size);
//     console.log(allFiles);
// };